import { useEffect, useMemo, useState } from 'react';
import { Bookmark, ChevronRight, Download, ExternalLink, Search, X } from 'lucide-react';
import type { LayawayRow } from '@contracts';
import { ODOO_SB_WEB_URL, ODOO_WEB_URL } from '@/lib/config';
import { canOpenOdooLinks } from '@/services/authService';
import { exportExcel } from '@/lib/exportData';

const money = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 2 });
const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const wishlistUrl = (id: number) => `${ODOO_WEB_URL}/web#id=${id}&model=pos.wishlist&view_type=form`;
const orderUrl = (id: number) => `${ODOO_WEB_URL}/web#id=${id}&model=pos.order&view_type=form`;
const sbOrderUrl = (id: number) => `${ODOO_SB_WEB_URL}/web#id=${id}&model=pos.order&view_type=form`;
/** Folio del apartado: VG → pos.wishlist @ salogaash; SB → la ORDEN ligada (pos.order)
 *  @ odoo-smartbamboo (advance.payments tiene regla de registro que bloquea al usuario). */
const folioUrl = (l: { id: number; source?: 'odoo' | 'sb'; orderId?: number | null }) =>
  l.source === 'sb'
    ? (l.orderId ? sbOrderUrl(l.orderId) : null)
    : wishlistUrl(l.id);

const STATE: Record<string, { label: string; cls: string }> = {
  active: { label: 'Activo', cls: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completado', cls: 'bg-emerald-100 text-emerald-700' },
  draft: { label: 'Borrador', cls: 'bg-slate-200 text-slate-600' },
  expired: { label: 'Expirado', cls: 'bg-rose-100 text-rose-700' },
};
const stateInfo = (s: string) => STATE[s] ?? { label: s || '—', cls: 'bg-slate-100 text-slate-600' };

/** Fuente del apartado: VG/Pielé (pos.wishlist) vs SmartBamboo (advance.payments). */
const SOURCE: Record<string, { label: string; short: string; cls: string; bar: string }> = {
  odoo: { label: 'Vine Garden / Pielé', short: 'VG / Pielé', cls: 'bg-fuchsia-100 text-fuchsia-700', bar: 'border-fuchsia-200 bg-fuchsia-50/70 text-fuchsia-700' },
  sb: { label: 'SmartBamboo', short: 'SmartBamboo', cls: 'bg-emerald-100 text-emerald-700', bar: 'border-emerald-200 bg-emerald-50/70 text-emerald-700' },
};
const srcMeta = (s?: string) => SOURCE[s ?? 'odoo'] ?? SOURCE.odoo;
const srcKey = (s?: string) => (s === 'sb' ? 'sb' : 'odoo');

/** Estado de la Orden de Abono (pos.order) → etiqueta es-MX. */
const ORDER_STATE: Record<string, string> = {
  done: 'Registrado', paid: 'Pagado', invoiced: 'Facturado', draft: 'Borrador', cancel: 'Cancelado',
};
const orderStateLabel = (s?: string) => (s ? ORDER_STATE[s] ?? s : '');

type SortCol = 'ref' | 'date' | 'pos' | 'customer' | 'paid' | 'pending' | 'total';
type SortDir = 'asc' | 'desc';
const PAGE_OPTS = [10, 50, 100, 0];

export interface LayawaysDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  layaways: LayawayRow[];
  loading?: boolean;
}

/** Drill-down de Apartados: el front solo pinta + filtra/ordena/pagina. */
export function LayawaysDrilldownModal({ open, onClose, title, layaways, loading }: LayawaysDrilldownModalProps) {
  const [q, setQ] = useState('');
  const [pos, setPos] = useState('');
  const [sortCol, setSortCol] = useState<SortCol>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => setPage(0), [q, pos, sortCol, sortDir, pageSize, layaways]);
  useEffect(() => setExpanded(new Set()), [layaways]);

  const toggleRow = (id: number) =>
    setExpanded((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const posNames = useMemo(
    () => Array.from(new Set(layaways.map((l) => l.posName).filter(Boolean))).sort(),
    [layaways],
  );

  const toggleSort = (col: SortCol) => {
    if (col === sortCol) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir(col === 'ref' || col === 'pos' || col === 'customer' ? 'asc' : 'desc');
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    let rows = pos ? layaways.filter((l) => l.posName === pos) : layaways;
    if (term) {
      rows = rows.filter((l) => [l.ref, l.customer, l.posName, l.orderRef].some((f) => f?.toLowerCase().includes(term)));
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const str = (a: string, b: string) => a.localeCompare(b) * dir;
    const sorted = [...rows];
    sorted.sort((a, b) => {
      // Agrupar por fuente: VG/Pielé (odoo) primero, SmartBamboo después.
      if (srcKey(a.source) !== srcKey(b.source)) return srcKey(a.source) === 'odoo' ? -1 : 1;
      switch (sortCol) {
        case 'date': return str(a.date, b.date);
        case 'paid': return (a.paid - b.paid) * dir;
        case 'pending': return (a.pending - b.pending) * dir;
        case 'total': return (a.total - b.total) * dir;
        case 'pos': return str(a.posName, b.posName);
        case 'customer': return str(a.customer, b.customer);
        default: return str(a.ref, b.ref);
      }
    });
    return sorted;
  }, [layaways, q, pos, sortCol, sortDir]);

  const sums = useMemo(
    () => filtered.reduce((a, l) => ({ paid: a.paid + l.paid, pending: a.pending + l.pending, total: a.total + l.total }), { paid: 0, pending: 0, total: 0 }),
    [filtered],
  );
  const groupStats = useMemo(() => {
    const m: Record<string, { count: number; paid: number; pending: number; total: number }> = {};
    for (const l of filtered) {
      const k = srcKey(l.source);
      const g = (m[k] ??= { count: 0, paid: 0, pending: 0, total: 0 });
      g.count++; g.paid += l.paid; g.pending += l.pending; g.total += l.total;
    }
    return m;
  }, [filtered]);
  const pages = pageSize ? Math.max(1, Math.ceil(filtered.length / pageSize)) : 1;
  const start = pageSize ? page * pageSize : 0;
  const rows = pageSize ? filtered.slice(start, start + pageSize) : filtered;

  const doExport = () =>
    void exportExcel(
      'apartados',
      filtered.map((l) => ({
        Empresa: srcMeta(l.source).label,
        Folio: l.ref,
        Fecha: l.date,
        Sucursal: l.posName,
        Cliente: l.customer,
        Abonado: l.paid,
        'Por cobrar': l.source === 'sb' ? '' : l.pending,
        Total: l.total,
        Estado: stateInfo(l.state).label,
        'Orden final': l.orderRef ?? '',
      })),
    );

  if (!open) return null;

  const arrow = (col: SortCol) => (sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '');
  const cols: { col: SortCol; label: string; right: boolean }[] = [
    { col: 'ref', label: 'Folio', right: false },
    { col: 'date', label: 'Fecha', right: false },
    { col: 'pos', label: 'Sucursal', right: false },
    { col: 'customer', label: 'Cliente', right: false },
    { col: 'paid', label: 'Abonado', right: true },
    { col: 'pending', label: 'Por cobrar', right: true },
    { col: 'total', label: 'Total', right: true },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="flex max-h-[90dvh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center gap-4 bg-gradient-to-r from-pink-600 via-fuchsia-600 to-purple-600 px-5 py-4 text-white">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-white/25 bg-white/15">
            <Bookmark className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-xl font-black leading-tight">{title}</h2>
            <p className="mt-0.5 truncate text-sm text-white/80">
              <span className="font-semibold text-white">{layaways.length} apartados</span>
              {' · Abonado '}<span className="font-semibold text-white">{money(sums.paid)}</span>
              {' · Por cobrar '}{money(sums.pending)}
            </p>
          </div>
          <button onClick={onClose} title="Cerrar" className="shrink-0 rounded-lg p-1.5 text-white/80 hover:bg-white/15">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-5 py-3">
          {posNames.length > 1 && (
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Sucursal</span>
              <select
                value={pos}
                onChange={(e) => setPos(e.target.value)}
                className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-fuchsia-400"
              >
                <option value="">Todas</option>
                {posNames.map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
            </div>
          )}
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar folio, cliente, sucursal…"
              className="w-full rounded-xl border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-fuchsia-400"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Mostrar</span>
            <div className="flex gap-1">
              {PAGE_OPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => setPageSize(p)}
                  className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold ${
                    pageSize === p ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {p === 0 ? 'Todos' : p}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={doExport}
            title="Descargar Excel"
            className="flex items-center gap-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:border-emerald-400 hover:text-emerald-600"
          >
            <Download className="h-4 w-4" /> Excel
          </button>
        </div>

        {/* Tabla */}
        <div className="min-h-0 flex-1 overflow-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-400">Cargando apartados…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-400">Sin apartados para los filtros actuales.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500 shadow-sm">
                <tr>
                  {cols.map((h) => (
                    <th
                      key={h.col}
                      onClick={() => toggleSort(h.col)}
                      className={`cursor-pointer select-none px-4 py-2.5 font-semibold hover:text-fuchsia-600 ${h.right ? 'text-right' : ''}`}
                    >
                      {h.label}
                      <span className="ml-0.5 inline-block w-2 text-fuchsia-600">{arrow(h.col)}</span>
                    </th>
                  ))}
                  <th className="px-4 py-2.5 font-semibold">Estado</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l, i) => {
                  const st = stateInfo(l.state);
                  const sm = srcMeta(l.source);
                  const abonos = l.abonos ?? [];
                  const hasAbonos = abonos.length > 0;
                  const isOpen = expanded.has(l.id);
                  const showGroup = i === 0 || srcKey(rows[i - 1].source) !== srcKey(l.source);
                  const g = groupStats[srcKey(l.source)];
                  const isSb = l.source === 'sb';
                  return [
                    showGroup ? (
                      <tr key={`${l.id}-g`} className="select-none">
                        <td colSpan={cols.length + 1} className={`border-y px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide ${sm.bar}`}>
                          <span className={`mr-2 inline-block rounded px-1.5 py-0.5 text-[10px] ${sm.cls}`}>{sm.label}</span>
                          {g ? `${g.count} apartados · Abonado ${money(g.paid)}${isSb ? '' : ` · Por cobrar ${money(g.pending)}`}` : ''}
                        </td>
                      </tr>
                    ) : null,
                    <tr
                      key={l.id}
                      onClick={hasAbonos ? () => toggleRow(l.id) : undefined}
                      title={hasAbonos ? 'Ver abonos' : undefined}
                      className={`border-b border-slate-100 ${hasAbonos ? 'cursor-pointer hover:bg-fuchsia-50/50' : 'hover:bg-slate-50'} ${isOpen ? 'bg-fuchsia-50/60' : ''}`}
                    >
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1">
                          <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''} ${hasAbonos ? '' : 'invisible'}`} />
                          {(() => {
                            const url = folioUrl(l);
                            return url && canOpenOdooLinks() ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title={l.source === 'sb' ? 'Abrir la orden ligada en Odoo SmartBamboo' : 'Abrir apartado en Odoo'}
                                className="group inline-flex items-center gap-1 font-mono text-xs font-semibold text-fuchsia-700 hover:underline"
                              >
                                {l.ref}
                                <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                              </a>
                            ) : (
                              <span className="font-mono text-xs font-semibold text-slate-600">{l.ref}</span>
                            );
                          })()}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500">{fmtDate(l.date)}</td>
                      <td className="px-4 py-2.5">{l.posName || '—'}</td>
                      <td className="px-4 py-2.5">{l.customer}</td>
                      <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-emerald-700">{money(l.paid)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-amber-700">{isSb ? <span className="text-slate-300">—</span> : money(l.pending)}</td>
                      <td className="px-4 py-2.5 text-right font-bold tabular-nums text-slate-700">{money(l.total)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${st.cls}`}>{st.label}</span>
                      </td>
                    </tr>,
                    isOpen && hasAbonos ? (
                      <tr key={`${l.id}-ab`} className="bg-slate-50">
                        <td colSpan={cols.length + 1} className="px-4 py-2">
                          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                            <div className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                              {isSb ? 'Pagos' : 'Órdenes de abono'} ({abonos.length}) · Total abonado {money(l.paid)}
                            </div>
                            {abonos.map((a, idx) => (
                              <div key={idx} className="flex items-center gap-3 border-b border-slate-50 px-3 py-1.5 text-xs last:border-0">
                                <span className="w-4 shrink-0 text-slate-400">{idx + 1}.</span>
                                {a.orderRef ? (
                                  canOpenOdooLinks() ? (
                                    <a
                                      href={a.orderId ? orderUrl(a.orderId) : undefined}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      title="Abrir orden de abono en Odoo"
                                      className="group inline-flex items-center gap-1 font-mono font-semibold text-fuchsia-700 hover:underline"
                                    >
                                      {a.orderRef}
                                      <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
                                    </a>
                                  ) : (
                                    <span className="font-mono font-semibold text-slate-700">{a.orderRef}</span>
                                  )
                                ) : (
                                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">{a.method ?? '—'}</span>
                                )}
                                <span className="text-slate-500">{fmtDate(a.date)}</span>
                                {a.cashier ? <span className="truncate text-slate-500">{a.cashier}</span> : null}
                                {a.state ? <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">{orderStateLabel(a.state)}</span> : null}
                                <span className={`ml-auto font-bold tabular-nums ${a.amount < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{money(a.amount)}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer: Abonado · Por cobrar · Total */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 bg-slate-50/60 px-5 py-3 text-sm">
          <span className="text-slate-600">
            Abonado ({filtered.length}): <span className="font-bold text-emerald-700">{money(sums.paid)}</span>
          </span>
          <span className="text-amber-600">Por cobrar: <span className="font-semibold">{money(sums.pending)}</span></span>
          <span className="text-slate-600">Total: <span className="font-bold text-slate-800">{money(sums.total)}</span></span>
          <div className="ml-auto flex items-center gap-3 text-slate-500">
            <span className="text-xs">
              {pageSize ? `${start + 1}–${Math.min(start + pageSize, filtered.length)} de ${filtered.length}` : `${filtered.length} apartados`}
            </span>
            {pageSize > 0 && pages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40">‹</button>
                <span className="text-xs">{page + 1} / {pages}</span>
                <button onClick={() => setPage((p) => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1} className="rounded-lg px-2 py-1 hover:bg-slate-200 disabled:opacity-40">›</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
